import { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Code2, Eye, Copy, Download, Maximize2, Minimize2, 
  Play, RefreshCw, FileCode, FileText, Image, BarChart2,
  X, Check, ExternalLink, Terminal
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';

/**
 * CanvasPreview - A Claude-style split-pane preview component
 * Renders code, HTML, charts, and other artifacts in a live preview pane
 */
export function CanvasPreview({ 
  content, 
  type = 'auto', // auto, code, html, markdown, image, chart
  language = 'javascript',
  title = 'Preview',
  onClose,
  isFullscreen = false,
  onToggleFullscreen
}) {
  const [activeTab, setActiveTab] = useState('preview');
  const [copied, setCopied] = useState(false);
  const iframeRef = useRef(null);
  const [detectedType, setDetectedType] = useState(type);

  // Auto-detect content type
  useEffect(() => {
    if (type === 'auto' && content) {
      if (content.startsWith('data:image/') || content.startsWith('http') && /\.(png|jpg|jpeg|gif|webp|svg)/.test(content)) {
        setDetectedType('image');
      } else if (content.includes('<!DOCTYPE html') || content.includes('<html') || (content.includes('<') && content.includes('>'))) {
        setDetectedType('html');
      } else if (content.startsWith('```') || /^(import|from|def|class|function|const|let|var|#|\/\/)/.test(content.trim())) {
        setDetectedType('code');
      } else if (content.includes('# ') || content.includes('## ') || content.includes('**')) {
        setDetectedType('markdown');
      } else {
        setDetectedType('text');
      }
    } else {
      setDetectedType(type);
    }
  }, [content, type]);

  const copyToClipboard = async () => {
    try {
      await navigator.clipboard.writeText(content);
      setCopied(true);
      toast.success('Copied to clipboard!');
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      toast.error('Failed to copy');
    }
  };

  const downloadContent = () => {
    let blob, filename;
    
    if (detectedType === 'image' && content.startsWith('data:image/')) {
      // Base64 image
      const byteString = atob(content.split(',')[1]);
      const mimeType = content.split(':')[1].split(';')[0];
      const ab = new ArrayBuffer(byteString.length);
      const ia = new Uint8Array(ab);
      for (let i = 0; i < byteString.length; i++) {
        ia[i] = byteString.charCodeAt(i);
      }
      blob = new Blob([ab], { type: mimeType });
      filename = `artifact.${mimeType.split('/')[1]}`;
    } else if (detectedType === 'html') {
      blob = new Blob([content], { type: 'text/html' });
      filename = 'artifact.html';
    } else if (detectedType === 'code') {
      const ext = language === 'python' ? 'py' : language === 'javascript' ? 'js' : 'txt';
      blob = new Blob([content], { type: 'text/plain' });
      filename = `artifact.${ext}`;
    } else {
      blob = new Blob([content], { type: 'text/plain' });
      filename = 'artifact.txt';
    }
    
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    a.click();
    URL.revokeObjectURL(url);
    toast.success('Downloaded!');
  };

  // Render HTML in iframe
  const renderHTML = () => {
    if (iframeRef.current && detectedType === 'html') {
      const doc = iframeRef.current.contentDocument;
      doc.open();
      doc.write(content);
      doc.close();
    }
  };

  useEffect(() => {
    if (activeTab === 'preview' && detectedType === 'html') {
      renderHTML();
    }
  }, [activeTab, content, detectedType]);

  // Extract code from markdown code blocks
  const cleanCode = (text) => {
    if (!text) return '';
    // Remove markdown code block markers
    return text.replace(/^```[\w]*\n?/gm, '').replace(/```$/gm, '').trim();
  };

  // Simple syntax highlighting
  const highlightCode = (code, lang) => {
    const keywords = {
      python: ['def', 'class', 'import', 'from', 'if', 'else', 'elif', 'for', 'while', 'return', 'try', 'except', 'with', 'as', 'None', 'True', 'False', 'and', 'or', 'not', 'in', 'is', 'lambda', 'async', 'await'],
      javascript: ['const', 'let', 'var', 'function', 'return', 'if', 'else', 'for', 'while', 'class', 'import', 'export', 'from', 'async', 'await', 'try', 'catch', 'new', 'this', 'true', 'false', 'null', 'undefined']
    };
    
    const langKeywords = keywords[lang] || keywords.javascript;
    let highlighted = code
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;');
    
    // Highlight strings
    highlighted = highlighted.replace(/(["'`])(?:(?!\1)[^\\]|\\.)*\1/g, '<span class="text-green-400">$&</span>');
    
    // Highlight comments
    highlighted = highlighted.replace(/(#.*$|\/\/.*$)/gm, '<span class="text-zinc-500">$&</span>');
    
    // Highlight keywords
    langKeywords.forEach(kw => {
      const regex = new RegExp(`\\b(${kw})\\b`, 'g');
      highlighted = highlighted.replace(regex, '<span class="text-purple-400">$1</span>');
    });
    
    // Highlight numbers
    highlighted = highlighted.replace(/\b(\d+\.?\d*)\b/g, '<span class="text-amber-400">$1</span>');
    
    return highlighted;
  };

  const getIcon = () => {
    switch (detectedType) {
      case 'code': return <Code2 className="w-4 h-4" />;
      case 'html': return <FileCode className="w-4 h-4" />;
      case 'image': return <Image className="w-4 h-4" />;
      case 'chart': return <BarChart2 className="w-4 h-4" />;
      default: return <FileText className="w-4 h-4" />;
    }
  };

  if (!content) return null;

  return (
    <motion.div
      initial={{ opacity: 0, x: 20 }}
      animate={{ opacity: 1, x: 0 }}
      exit={{ opacity: 0, x: 20 }}
      className={`bg-zinc-900 border border-zinc-800 rounded-xl overflow-hidden flex flex-col ${
        isFullscreen ? 'fixed inset-4 z-50' : 'h-full'
      }`}
      data-testid="canvas-preview"
    >
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-2 border-b border-zinc-800 bg-zinc-900/80">
        <div className="flex items-center gap-2">
          <div className="flex items-center gap-1.5 text-zinc-400">
            {getIcon()}
            <span className="text-sm font-medium">{title}</span>
          </div>
          <span className="text-xs text-zinc-600 px-2 py-0.5 rounded bg-zinc-800">
            {detectedType.toUpperCase()}
          </span>
        </div>
        
        <div className="flex items-center gap-1">
          {/* Tab switches for code/HTML */}
          {(detectedType === 'code' || detectedType === 'html') && (
            <div className="flex bg-zinc-800 rounded-lg p-0.5 mr-2">
              <button
                onClick={() => setActiveTab('preview')}
                className={`px-3 py-1 text-xs rounded-md transition-colors ${
                  activeTab === 'preview' 
                    ? 'bg-teal-500/20 text-teal-400' 
                    : 'text-zinc-500 hover:text-zinc-300'
                }`}
              >
                <Eye className="w-3 h-3 inline mr-1" />
                Preview
              </button>
              <button
                onClick={() => setActiveTab('code')}
                className={`px-3 py-1 text-xs rounded-md transition-colors ${
                  activeTab === 'code' 
                    ? 'bg-teal-500/20 text-teal-400' 
                    : 'text-zinc-500 hover:text-zinc-300'
                }`}
              >
                <Code2 className="w-3 h-3 inline mr-1" />
                Code
              </button>
            </div>
          )}
          
          <Button
            variant="ghost"
            size="icon"
            className="h-7 w-7 text-zinc-500 hover:text-white"
            onClick={copyToClipboard}
            title="Copy"
          >
            {copied ? <Check className="w-4 h-4 text-green-400" /> : <Copy className="w-4 h-4" />}
          </Button>
          
          <Button
            variant="ghost"
            size="icon"
            className="h-7 w-7 text-zinc-500 hover:text-white"
            onClick={downloadContent}
            title="Download"
          >
            <Download className="w-4 h-4" />
          </Button>
          
          {onToggleFullscreen && (
            <Button
              variant="ghost"
              size="icon"
              className="h-7 w-7 text-zinc-500 hover:text-white"
              onClick={onToggleFullscreen}
              title={isFullscreen ? 'Exit fullscreen' : 'Fullscreen'}
            >
              {isFullscreen ? <Minimize2 className="w-4 h-4" /> : <Maximize2 className="w-4 h-4" />}
            </Button>
          )}
          
          {onClose && (
            <Button
              variant="ghost"
              size="icon"
              className="h-7 w-7 text-zinc-500 hover:text-red-400"
              onClick={onClose}
              title="Close"
            >
              <X className="w-4 h-4" />
            </Button>
          )}
        </div>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-auto">
        {/* Image Preview */}
        {(detectedType === 'image' || detectedType === 'chart') && (
          <div className="p-4 flex items-center justify-center min-h-[300px] bg-zinc-950">
            <img 
              src={content} 
              alt={title}
              className="max-w-full max-h-[60vh] rounded-lg shadow-xl"
            />
          </div>
        )}

        {/* HTML Preview */}
        {detectedType === 'html' && activeTab === 'preview' && (
          <div className="h-full min-h-[300px]">
            <iframe
              ref={iframeRef}
              className="w-full h-full bg-white"
              sandbox="allow-scripts allow-same-origin"
              title="HTML Preview"
            />
          </div>
        )}

        {/* Code View (for code or HTML source) */}
        {((detectedType === 'code') || (detectedType === 'html' && activeTab === 'code')) && (
          <div className="p-4 bg-zinc-950 min-h-[300px]">
            <pre className="text-sm text-zinc-300 font-mono overflow-x-auto">
              <code 
                dangerouslySetInnerHTML={{ 
                  __html: highlightCode(cleanCode(content), language) 
                }}
              />
            </pre>
          </div>
        )}

        {/* Markdown/Text View */}
        {(detectedType === 'markdown' || detectedType === 'text') && (
          <div className="p-4 prose prose-invert prose-sm max-w-none">
            <pre className="text-sm text-zinc-300 whitespace-pre-wrap font-sans">
              {content}
            </pre>
          </div>
        )}
      </div>

      {/* Footer with metadata */}
      <div className="px-4 py-2 border-t border-zinc-800 bg-zinc-900/50 flex items-center justify-between text-xs text-zinc-500">
        <span>{content?.length?.toLocaleString() || 0} characters</span>
        <span>
          {detectedType === 'code' && language && `Language: ${language}`}
          {detectedType === 'image' && 'Image artifact'}
          {detectedType === 'html' && 'HTML document'}
        </span>
      </div>
    </motion.div>
  );
}

/**
 * SplitPaneView - A container that can show chat + canvas side by side
 */
export function SplitPaneView({ 
  children, 
  artifact, 
  artifactType,
  artifactTitle,
  onCloseArtifact
}) {
  const [isFullscreen, setIsFullscreen] = useState(false);

  return (
    <div className="flex h-full gap-4">
      {/* Main content (chat) */}
      <div className={`flex-1 min-w-0 transition-all duration-300 ${artifact ? 'lg:w-1/2' : 'w-full'}`}>
        {children}
      </div>

      {/* Canvas/Artifact pane */}
      <AnimatePresence>
        {artifact && (
          <motion.div
            initial={{ width: 0, opacity: 0 }}
            animate={{ width: '50%', opacity: 1 }}
            exit={{ width: 0, opacity: 0 }}
            transition={{ duration: 0.3 }}
            className="hidden lg:block"
          >
            <CanvasPreview
              content={artifact}
              type={artifactType}
              title={artifactTitle || 'Artifact'}
              onClose={onCloseArtifact}
              isFullscreen={isFullscreen}
              onToggleFullscreen={() => setIsFullscreen(!isFullscreen)}
            />
          </motion.div>
        )}
      </AnimatePresence>

      {/* Mobile: Show artifact as overlay */}
      <AnimatePresence>
        {artifact && (
          <motion.div
            initial={{ opacity: 0, y: 100 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 100 }}
            className="lg:hidden fixed inset-x-0 bottom-0 z-50 max-h-[60vh] bg-zinc-900 rounded-t-2xl border-t border-zinc-800 shadow-2xl"
          >
            <CanvasPreview
              content={artifact}
              type={artifactType}
              title={artifactTitle || 'Artifact'}
              onClose={onCloseArtifact}
            />
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

export default CanvasPreview;
